import { useMemo } from 'react';
import { Grid, Typography, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useGrafanaQuery } from '../hooks/useGrafanaQuery';
import {
  buildInfraStatQueries,
  buildInfraTimeSeriesQueries,
  INFRA_STAT_PANELS,
  INFRA_TIME_PANELS,
} from '../queries/infrastructure';
import { transformToTimeSeries, transformToInstantValue } from '../utils/transformDataFrame';
import { StatCard } from './charts/StatCard';
import { TimeSeriesChart } from './charts/TimeSeriesChart';
import { ChaosExperimentPanel } from './ChaosExperimentPanel';
import type { EntityFilters } from '../hooks/useEntityFilters';

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1, 0),
  },
}));

interface InfrastructureDashboardContentProps {
  filters: EntityFilters;
}

export const InfrastructureDashboardContent = ({ filters }: InfrastructureDashboardContentProps) => {
  const classes = useStyles();
  const { entity } = useEntity();

  const namespace =
    entity.metadata.annotations?.['backstage.io/kubernetes-namespace'] ?? 'demo-apps';
  const app = entity.metadata.name;
  const preset = filters.activePreset ?? '1h';

  // ── Stat queries ──────────────────────────────────────────
  const statQueries = useMemo(
    () => buildInfraStatQueries(namespace, app, preset),
    [namespace, app, preset],
  );
  const stats = useGrafanaQuery(statQueries, filters.timeRange);

  // ── Time series queries ───────────────────────────────────
  const tsQueries = useMemo(
    () => buildInfraTimeSeriesQueries(namespace, app, preset),
    [namespace, app, preset],
  );
  const ts = useGrafanaQuery(tsQueries, filters.timeRange);

  // ── Transform data ────────────────────────────────────────
  const statValues = useMemo(() => {
    if (!stats.data) return {};
    const result: Record<string, number | null> = {};
    for (const panel of INFRA_STAT_PANELS) {
      result[panel.refId] = transformToInstantValue(stats.data, panel.refId);
    }
    return result;
  }, [stats.data]);

  const restartSeries = useMemo(
    () => (ts.data ? transformToTimeSeries(ts.data, INFRA_TIME_PANELS.restartHistory.refId) : []),
    [ts.data],
  );

  const cpuSeries = useMemo(
    () => (ts.data ? transformToTimeSeries(ts.data, INFRA_TIME_PANELS.cpuOverTime.refId) : []),
    [ts.data],
  );

  const memorySeries = useMemo(
    () => (ts.data ? transformToTimeSeries(ts.data, INFRA_TIME_PANELS.memoryOverTime.refId) : []),
    [ts.data],
  );

  return (
    <Box>
      {/* ── Pod Health Overview ──────────────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 16 }}>
        Pod Health
      </Typography>
      <Grid container spacing={2}>
        {INFRA_STAT_PANELS.slice(0, 4).map(panel => (
          <Grid item xs={6} sm={3} key={panel.refId}>
            <StatCard
              title={panel.title}
              value={statValues[panel.refId] ?? null}
              unit={panel.unit}
              thresholds={panel.thresholds}
              loading={stats.loading}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Node Resource Stats ──────────────────────────── */}
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        {INFRA_STAT_PANELS.slice(4).map(panel => (
          <Grid item xs={6} sm={3} key={panel.refId}>
            <StatCard
              title={panel.title}
              value={statValues[panel.refId] ?? null}
              unit={panel.unit}
              thresholds={panel.thresholds}
              loading={stats.loading}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── Resource Utilisation Charts ──────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
        Resource Utilisation
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoCard title={INFRA_TIME_PANELS.cpuOverTime.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={cpuSeries}
                unit={INFRA_TIME_PANELS.cpuOverTime.unit}
                thresholdLine={INFRA_TIME_PANELS.cpuOverTime.thresholdLine}
                loading={ts.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoCard title={INFRA_TIME_PANELS.memoryOverTime.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={memorySeries}
                unit={INFRA_TIME_PANELS.memoryOverTime.unit}
                thresholdLine={INFRA_TIME_PANELS.memoryOverTime.thresholdLine}
                loading={ts.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
      </Grid>

      {/* ── Pod Restart History ──────────────────────────── */}
      <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
        Pod Restart History
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InfoCard title={INFRA_TIME_PANELS.restartHistory.title} noPadding>
            <Box p={1}>
              <TimeSeriesChart
                series={restartSeries}
                unit={INFRA_TIME_PANELS.restartHistory.unit}
                loading={ts.loading}
              />
            </Box>
          </InfoCard>
        </Grid>
      </Grid>

      {/* ── Chaos Mesh ───────────────────────────────────── */}
      {entity.metadata.annotations?.['chaos-mesh/enabled'] === 'true' && (
        <ChaosExperimentPanel entity={entity} />
      )}
    </Box>
  );
};
